import TimeAxisControlButton from "./TimeAxisControlButton";
import * as d3 from "d3";
import SlidingIcon from "@mui/icons-material/KeyboardTab";
import FocusIcon from "@mui/icons-material/VerticalAlignCenter";
import GoToRangeIcon from "@mui/icons-material/Expand";
import { BREAKPOINT_MOBILE, getIsIe11 } from "../../../constants/constants";
import styled from "styled-components/macro";
import { ZoomTransform } from "d3";
import { TIME_AXIS_MARGIN_HZ } from "../TimelinesStyles";
import { useDashboardPageContext } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useSelector } from "store/useSelector";
import { useDispatch } from "react-redux";
import { setFocusRange } from "providers/GlobalStateProvider";
import { useTimelineTooltipStore } from "./TimelineTooltip/timelineTooltipStore";

const TimeAxisControlsStyles = styled.div`
  pointer-events: none;
  button {
    pointer-events: auto;
  }
  .focusIcon {
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    -o-transform: rotate(90deg);
    transform: rotate(90deg);
    padding: 0;
    margin: 0;
  }
  font-family: "GothamMedium";
  position: absolute;
  margin-right: -5px;
  top: -36px;
  right: ${TIME_AXIS_MARGIN_HZ}px;
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    right: ${TIME_AXIS_MARGIN_HZ * 1.5}px;
  }
  width: fit-content;
  transform-origin: right;
  transform: scale(0.8);
  &.internetExplorer {
    margin-right: -16px;
    position: fixed;
  }
  .timeAxisControlsButtonsWrapper {
    display: flex;
  }
  &.singleTimeline {
    top: -34px;
    right: ${TIME_AXIS_MARGIN_HZ}px;
  }
  /* when tooltip is open, hide buttons (instead of making them z-index: -1, which would require another stacking context, consuming significant memory on iOS Safari which is memory-throttled in order to nerf web apps and preserve Apple's 30% highway robbery monopoly on all app store apps, thanks Tim) */
  &.tooltipOpen {
    display: none;
  }
`;

export default function TimeAxisControls({
  isSingleTimeline,
  isSlidingAttached,
  lt,
  state,
  resetToFocus,
  isSlideValid,
  resetToSliding,
}: {
  isSingleTimeline: boolean;
  isSlidingAttached: boolean;
  lt: number;
  state: { isSliding: boolean; zoomTransform: ZoomTransform };
  resetToFocus: (shouldReset: boolean) => void;
  isSlideValid: (time: number) => boolean;
  resetToSliding: (shouldReset: boolean) => void;
}) {
  const isIE = getIsIe11();
  const { translate } = useFlightTranslation();
  const { isFullDaySelected } = useDashboardPageContext();
  const dispatch = useDispatch();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const [{ modal }] = useTimelineTooltipStore();

  return (
    <TimeAxisControlsStyles
      className={`timeAxisControlsStyles${isIE ? " internetExplorer" : ""}${
        isSingleTimeline ? " singleTimeline" : ""
      }${modal ? " tooltipOpen" : ""}`}
      data-component-name={"Time Axis Controls"}
    >
      <div className="timeAxisControlsButtonsWrapper">
        <TimeAxisControlButton
          tooltipTitle={translate(`Go_to_Visible_Range`)}
          isAvailable={
            Boolean(visibleRange) && state.zoomTransform !== d3.zoomIdentity
          }
          onClick={() => visibleRange && dispatch(setFocusRange(visibleRange))}
        >
          <GoToRangeIcon style={{ transform: "rotate(90deg)" }} />
        </TimeAxisControlButton>
        <TimeAxisControlButton
          tooltipTitle={translate(
            `Reset_Focus_to_${isFullDaySelected ? "Day" : "Shift"}`
          )}
          isAvailable={state.zoomTransform !== d3.zoomIdentity}
          onClick={() => resetToFocus(true)}
        >
          <FocusIcon className="focusIcon" />
        </TimeAxisControlButton>
        <TimeAxisControlButton
          tooltipTitle={translate("Live_View")}
          isAvailable={
            isSlideValid(lt) && (!state.isSliding || !isSlidingAttached)
          }
          onClick={() => resetToSliding(true)}
        >
          <SlidingIcon />
        </TimeAxisControlButton>
      </div>
    </TimeAxisControlsStyles>
  );
}
